import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSliders, FiX, FiChevronDown } from 'react-icons/fi';
import tmdb from '../api/tmdb';
import MovieCard from '../components/MovieCard';
import GenreFilter from '../components/GenreFilter';
import Loader from '../components/Loader';
import Footer from '../components/Footer';

const SORT_OPTIONS = [
  { value: 'popularity.desc',     label: 'Most Popular' },
  { value: 'vote_average.desc',   label: 'Highest Rated' },
  { value: 'release_date.desc',   label: 'Newest First' },
  { value: 'release_date.asc',    label: 'Oldest First' },
  { value: 'revenue.desc',        label: 'Highest Revenue' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 40 }, (_, i) => currentYear - i);

const Select = ({ value, onChange, options, placeholder }) => (
  <div className="relative">
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full appearance-none px-4 py-2.5 pr-9 rounded-xl text-sm font-body outline-none cursor-pointer"
      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: value ? 'var(--text)' : 'var(--muted)' }}>
      <option value="">{placeholder}</option>
      {options.map(o => (
        <option key={o.value || o} value={o.value || o} style={{ background: '#111118' }}>
          {o.label || o}
        </option>
      ))}
    </select>
    <FiChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'var(--muted)' }} />
  </div>
);

const MoviesPage = () => {
  const [movies,     setMovies]     = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [genre,      setGenre]      = useState(null);
  const [sortBy,     setSortBy]     = useState('popularity.desc');
  const [year,       setYear]       = useState('');
  const [minRating,  setMinRating]  = useState('');
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilter, setShowFilter] = useState(false);

  const activeFilters = [year, minRating, sortBy !== 'popularity.desc'].filter(Boolean).length;

  const load = useCallback((g, p, append = false) => {
    setLoading(true);
    const params = {
      sort_by:       sortBy,
      page:          p,
      ...(g    && { with_genres: g }),
      ...(year && { 'primary_release_year': year }),
      ...(minRating && { 'vote_average.gte': minRating, 'vote_count.gte': 100 }),
    };
    tmdb.get('/discover/movie', { params })
      .then(res => {
        setMovies(prev => append ? [...prev, ...res.data.results] : res.data.results);
        setTotalPages(res.data.total_pages || 1);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [sortBy, year, minRating]);

  useEffect(() => {
    setPage(1);
    load(genre, 1, false);
  }, [genre, sortBy, year, minRating, load]);

  const loadMore = () => { const n = page + 1; setPage(n); load(genre, n, true); };

  const clearFilters = () => { setSortBy('popularity.desc'); setYear(''); setMinRating(''); };

  return (
    <div style={{ background: 'var(--dark)', minHeight: '100vh', paddingTop: 88 }}>
      <div className="max-w-screen-xl mx-auto px-6 md:px-12">

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <h1 className="font-display text-4xl md:text-5xl tracking-wide" style={{ color: 'var(--text)' }}>Browse Movies</h1>
              <p className="font-body text-sm mt-1" style={{ color: 'var(--muted)' }}>
                {movies.length > 0 ? `${movies.length}+ films` : 'Explore thousands of films'}
              </p>
            </div>
            <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              onClick={() => setShowFilter(o => !o)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-body font-medium relative"
              style={{
                background: showFilter ? 'var(--red)' : 'rgba(255,255,255,0.06)',
                border: `1px solid ${showFilter ? 'rgba(229,9,20,0.5)' : 'rgba(255,255,255,0.08)'}`,
                color: showFilter ? '#fff' : 'var(--text)',
              }}>
              <FiSliders size={15} /> Filters
              {activeFilters > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-xs font-accent"
                  style={{ background: 'var(--red)', color: '#fff', fontSize: 10 }}>
                  {activeFilters}
                </span>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* Advanced filter panel */}
        <AnimatePresence>
          {showFilter && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-6">
              <div className="rounded-2xl p-5 grid grid-cols-1 sm:grid-cols-3 gap-4"
                style={{ background: 'rgba(17,17,24,0.85)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <div>
                  <label className="text-xs font-accent uppercase tracking-wider mb-2 block" style={{ color: 'var(--muted)' }}>Sort By</label>
                  <Select value={sortBy} onChange={setSortBy} options={SORT_OPTIONS} placeholder="Sort by…" />
                </div>
                <div>
                  <label className="text-xs font-accent uppercase tracking-wider mb-2 block" style={{ color: 'var(--muted)' }}>Release Year</label>
                  <Select value={year} onChange={setYear} options={YEARS} placeholder="Any year" />
                </div>
                <div>
                  <label className="text-xs font-accent uppercase tracking-wider mb-2 block" style={{ color: 'var(--muted)' }}>Min IMDb Rating</label>
                  <Select value={minRating} onChange={setMinRating}
                    options={[9,8,7,6,5].map(n => ({ value: n, label: `${n}+ stars` }))}
                    placeholder="Any rating" />
                </div>
                {activeFilters > 0 && (
                  <div className="sm:col-span-3 flex justify-end">
                    <button onClick={clearFilters}
                      className="flex items-center gap-1.5 text-xs font-body hover:text-white transition-colors"
                      style={{ color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
                      <FiX size={13} /> Clear filters
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Genre pills */}
        <div className="mb-8 -mx-6 md:-mx-12">
          <GenreFilter selected={genre} onSelect={(g) => { setGenre(g); setPage(1); }} />
        </div>

        {/* Grid */}
        {loading && movies.length === 0 ? <Loader /> : (
          <>
            <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(155px,1fr))' }}>
              {movies.map((movie, i) => (
                <motion.div key={movie.id + '-' + i}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (i % 20) * 0.025 }} className="flex justify-center">
                  <MovieCard movie={movie} />
                </motion.div>
              ))}
            </div>

            {page < totalPages && (
              <div className="flex justify-center mt-10 mb-4">
                <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                  onClick={loadMore} disabled={loading}
                  className="px-10 py-3 rounded-full font-body font-semibold text-sm"
                  style={{ background: loading ? 'rgba(229,9,20,0.4)' : 'var(--red)', color: '#fff', boxShadow: '0 0 20px rgba(229,9,20,0.3)' }}>
                  {loading ? 'Loading…' : 'Load More'}
                </motion.button>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default MoviesPage;
